import { useEffect, useMemo, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
// import { Document, Page } from "react-pdf";
import { useNavigate, useParams } from "react-router-dom";
import { forwardArrowIcon, PrintIcon, tippingCopy, tippingNavigate } from "../../assests/Icons/icon";
import multipleQR from "../../assests/multipleQR.svg";
import qrImg from '../../assests/qrImg.svg';
import singleQR from "../../assests/singleQR.svg";
import uploadIcon from "../../assests/uploadIcon.svg";
import dollarIcon from "../../assests/svg/dollarIcon.svg";
import { Circles } from "react-loader-spinner";
import { copyToClipBoard, downloadImage, downloadPDF, getCurrentUserData, isFeePaidByOrganization, notifyBugsnagError, printPDF, showToast } from "../../common/utils/Functions";
import TippingJourneyLayout from "../../components/layouts/TippingJourneyLayout";
import PaymentHeader from "../../components/paymentHeader/PaymentHeader";
import ReactSelectField from "../../components/theme/ReactSelectField";
import ThemeButton from "../../components/theme/ThemeButton";
import AmountField from "../../components/theme/amountField";
import useFetch from "../../hooks/useFetch";
import { WIZARD_TRACK_CLICK_TYPE, WizardTrackClickType } from "../../types/wizardTrackClickType";
import styles from "./tippingExperience.module.scss";
import { Document, Page, pdfjs } from 'react-pdf';
import toptipper from "../../assests/Logo.svg";
import { initPdfWorker } from '../../common/utils/pdfWorker';

initPdfWorker();
type Step = {
    id: string;
    title: string;
    subtitle: string;
    description?: string;
};

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


const steps: Step[] = [
    {
        id: "customize",
        title: "Customize Your Tip Page",
        subtitle: "Set your brand logo",
        description:
            "Your tipping screen is what your customers will see. You can customize it with your own logo, or use the TopTipper logo provided.",
    },
    {
        id: "qr",
        title: "Create QR Code and Link",
        subtitle: "Instantly test your QR code and link",
        description:
            "With TopTipper, you can create separate QR codes for properties, employees, or services. Would you like multiple QR codes or just one simple code?",
    },
    {
        id: "share",
        title: "Share Your Page",
        subtitle: "Print a sign or share your unique link",
        description:
            "Scan your QR Code and click your unique link.  You can send yourself a $1 tip to see how it works.",
    },
];

const showNotesOptions = [
    { label: 'Yes', value: 1 },
    { label: 'No', value: 0 }
];

const getColorPickerValue = (value: string | undefined, fallback: string) =>
    /^#([0-9A-F]{6})$/i.test(value || "") ? value as string : fallback;

const sanitizeHexInput = (rawValue: string) => {
    if (!rawValue) return "";
    const trimmed = rawValue.startsWith("#") ? rawValue.slice(1) : rawValue;
    const stripped = trimmed.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    return stripped ? `#${stripped}` : "#";
};

type ColorField = "theme_color" | "font_color";
type SuggestedAmountFieldKey = 'suggested_amount_1' | 'suggested_amount_2' | 'suggested_amount_3';

const TippingExperience = () => {
    const { uuid } = useParams();
    const data = getCurrentUserData(`${uuid}`);
    const userData = getCurrentUserData(`${uuid}`);
    const [imagePreview, setImagePreview] = useState({
        url: "",
        name: "",
    });
    const [logoImageLoaded, setLogoImageLoaded] = useState(true);
    const [brandingData, setBrandingData] = useState<any | null>(null);

    const [activeStep, setActiveStep] = useState(0);
    const [maxStepReached, setMaxStepReached] = useState(0);
    const [selectedQRType, setSelectedQRType] = useState<'single' | 'multiple' | null>(null);
    const [qrSubScreen, setQrSubScreen] = useState<'selection' | 'single-preview' | 'multiple-dashboard'>('selection');
    const [showSuggestedFields, setShowSuggestedFields] = useState(true);
    const [showTippingViewNote, setShowTippingViewNote] = useState(false);
    const [signDetails, setSignDetails] = useState<any | null>(null);
    const [signData, setSignData] = useState<any | null>(null);
    const [signPdfLoading, setSignPdfLoading] = useState(true);
    const [signPdfError, setSignPdfError] = useState<string>("");
    const [useIframePreview, setUseIframePreview] = useState(false);
    const [printDisabled, setPrintDisabled] = useState(false);
    const [downloadDisabled, setDownloadDisabled] = useState(false);
    const [isLogoDragActive, setIsLogoDragActive] = useState(false);
    const [pdfWidth, setPdfWidth] = useState(380);
    const signCardRef = useRef<HTMLDivElement>(null);
    const suggestedInitRef = useRef(false);
    const signApiCalledRef = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        const el = signCardRef.current;
        if (!el) return;
        const compute = () => {
            setPdfWidth(Math.floor(el.getBoundingClientRect().width) - 2);
        };
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        compute();
        return () => ro.disconnect();
    }, []);

    const handleColorHexChange = (field: ColorField, value: string) => {
        if (value === "") {
            setBrandingData((prev: any) => ({
                ...(prev || {}),
                [field]: "",
            }));
            return;
        }
        const sanitized = sanitizeHexInput(value);
        setBrandingData((prev: any) => ({
            ...(prev || {}),
            [field]: sanitized,
        }));
    };

    const handleColorPickerChange = (field: ColorField, value: string) => {
        setBrandingData((prev: any) => ({
            ...(prev || {}),
            [field]: value,
        }));
    };

    // Format amounts: show no decimals when .00, otherwise show two decimals
    const formatAmountDisplay = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return "";
        const num = Number(value);
        if (!Number.isFinite(num)) return "";
        return Number.isInteger(num) ? String(num) : num.toFixed(2);
    };

    const handleSuggestedAmountChange = (field: SuggestedAmountFieldKey) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const raw = event.target.value;
            const parsed = raw === "" ? null : Number(raw);
            setBrandingData((prev: any) => ({
                ...(prev || {}),
                [field]: Number.isNaN(parsed) ? null : parsed,
            }));
        };

    const themeColorPickerValue = useMemo(
        () => getColorPickerValue(brandingData?.theme_color, "#457392"),
        [brandingData?.theme_color],
    );

    const fontColorPickerValue = useMemo(
        () => getColorPickerValue(brandingData?.font_color, "#05111C"),
        [brandingData?.font_color],
    );


    const ColorInput = ({
        label,
        placeholder,
        field,
        value,
    }: {
        label: string;
        placeholder: string;
        field: ColorField;
        value?: string;
    }) => {
        const colorInputRef = useRef<HTMLInputElement | null>(null);
        const displayColor =
            field === "theme_color" ? themeColorPickerValue : fontColorPickerValue;

        return (
            <div>
                <label>{label}</label>
                <div className={styles.colorField}>
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={value || ""}
                        onChange={(e) => handleColorHexChange(field, e.target.value)}
                    />
                    <button
                        type="button"
                        className={styles.colorSwatchBtn}
                        style={{ backgroundColor: displayColor }}
                        onClick={() => colorInputRef.current?.click()}
                    />
                    <input
                        type="color"
                        ref={colorInputRef}
                        className={styles.hiddenColorInput}
                        value={displayColor}
                        onChange={(e) => handleColorPickerChange(field, e.target.value)}
                    />
                </div>
            </div>
        );
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        // Once the journey begins, clear the flags so we don't show the welcome modals again
        localStorage.removeItem("showTippingModal");
        localStorage.removeItem("showRatingsPrompt");
    }, []);

    const isLastStep = useMemo(
        () => activeStep === steps.length - 1,
        [activeStep],
    );

    const [countApi, { loading: countLoading }] = useFetch("/dashboard/count");

    const goToDashboard = () => {
        countApi()
            .then((res: any) => {
                const totalTips = res?.data?.totalTips ?? 0;
                localStorage.setItem("total_tips", String(totalTips));
                if (totalTips === 0) {
                    navigate(uuid ? `/${uuid}/QR-Codes-&-links` : "/QR-Codes-&-links");
                } else {
                    navigate(uuid ? `/${uuid}/dashboard` : "/dashboard");
                }
            })
            .catch(() => {
                navigate(uuid ? `/${uuid}/QR-Codes-&-links` : "/QR-Codes-&-links");
            });
    };

    const [
        viewProfileApi,
        { response: viewProfile },
    ] = useFetch(`/organization/view/${data?.uuid}`);

    /** ------ @updateProfileApi -------- */
    const [
        updateProfileApi,
        {
            loading: updateProfileLoading,
        },
    ] = useFetch(
        `/organization/customize/${data?.uuid}`,
        { method: "put" }
    );

    /** Api definition to get media response */
    const [mediaApi, { response: mediaResponse, loading: mediaLoading }] = useFetch(
        "/common/upload-media",
        {
            method: "post",
        },
        "formdata",
        true
    );

    /** Api for fetching sign style */
    const [
        signSizeStyleApi,
        { response: signSizeStyleResponse, loading: signStyleLoading, error: signStyleError },
    ] = useFetch(`/common/qr-signs`, {
        method: "get",
    });

    /** -----@signscardApi ------ */
    const [
        signsCardApi,
        {
            response: signsCardsResponse,
            loading: signsCardLoading,
            error: signsCardError,
        },
    ] = useFetch("/common/qr-sign/add", {
        method: "post",
    });

    // Api for tracking clicks
    const [trackClicksApi] = useFetch("/common/track-clicks", {
        method: "put",
    });

    const trackWizardClick = (type: WizardTrackClickType) => {
        trackClicksApi({ type }).catch(() => {
            
        });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                signSizeStyleApi();
            } catch (e: any) {
                notifyBugsnagError(e, {
                    api: "propertyListApi",
                });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (signSizeStyleResponse?.data) {
            // 8.5x11 letter size. Match by id so a renamed label still resolves.
            const size85x11 = signSizeStyleResponse.data.qrSizes?.find((item: any) => item.id === 3);

            // Printable Tip Sign. Match by id so a renamed label still resolves.
            const printableSign = signSizeStyleResponse.data.qrSigns?.find((item: any) => item.id === 5);

            if (size85x11 && printableSign) {
                setSignDetails({
                    sign_size_uuid: size85x11.uuid,
                    sign_uuid: printableSign.uuid,
                    size_name: size85x11.name,
                    sign_name: printableSign.name
                });
            }
        }
    }, [signSizeStyleResponse]);

    // Trigger sign creation API when step 2 is reached (only once)
    useEffect(() => {
        if (activeStep === 2 && signDetails?.sign_size_uuid && signDetails?.sign_uuid && data?.organization_details?.id && !signApiCalledRef.current) {
            const payload = {
                sign_size_uuid: signDetails.sign_size_uuid,
                sign_uuid: signDetails.sign_uuid,
                sign_for: 1,
                ids: [data.organization_details.id],
                is_default_sign: 1,
            };

            signApiCalledRef.current = true;

            try {
                signsCardApi(payload);
            } catch (e: any) {
                signApiCalledRef.current = false; // Reset on error so it can retry
                notifyBugsnagError(e, {
                    api: "signsCardApi",
                    payload,
                });
            }
        }

        // Reset the ref when leaving step 2
        if (activeStep !== 2) {
            signApiCalledRef.current = false;
        }
    }, [activeStep]);

    useEffect(() => {
        if (signsCardsResponse) {
            setSignData(signsCardsResponse.data);
        }
    }, [signsCardsResponse]);

    useEffect(() => {
        if (signData?.[0]?.url) {
            setSignPdfLoading(true);
            setSignPdfError("");
            setUseIframePreview(false);
        } else {
            setSignPdfLoading(false);
            setSignPdfError("PDF URL not available");
            setUseIframePreview(false);
        }
    }, [signData?.[0]?.url]);


    const handlePrintClick = () => {
        if (!signData?.[0]?.url || printDisabled) return;
        trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICK_WIZARD_PRINT_SIGN);
        setPrintDisabled(true);
        Promise.resolve(printPDF(signData[0].url)).finally(() => {
            setTimeout(() => setPrintDisabled(false), 2000);
        });
    };

    const handleDownloadClick = () => {
        if (!signData?.[0]?.url || downloadDisabled) return;
        trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICK_WIZARD_DOWNLOAD_SIGN);
        setDownloadDisabled(true);
        Promise.resolve(downloadPDF(signData[0].url, "qr-code-sign.pdf")).finally(() => {
            setTimeout(() => setDownloadDisabled(false), 2000);
        });
    };


    useEffect(() => {
        if (viewProfile?.data) {
            // Initialize branding data and image preview from organization details
            setBrandingData((prev: any) => ({
                ...(prev || {}),
                ...viewProfile.data,
                // ensure media_id is populated from organization media details
                media_id: viewProfile.data.organization_media_details?.id ?? prev?.media_id ?? null,
            }));
            if (viewProfile.data.organization_media_details) {
                setImagePreview({
                    url: viewProfile.data.organization_media_details.url,
                    name: viewProfile.data.organization_media_details.name,
                });
                setLogoImageLoaded(false);
            }
        }
    }, [viewProfile]);

    // Keep local toggle state in sync with brandingData.show_notes
    useEffect(() => {
        if (brandingData && typeof brandingData.show_notes !== "undefined") {
            setShowTippingViewNote(brandingData.show_notes === 1);
        }
    }, [brandingData?.show_notes]);

    useEffect(() => {
        if (mediaResponse?.data?.[0]) {
            const uploaded = mediaResponse.data[0];
            setImagePreview({
                url: uploaded.url,
                name: uploaded.name,
            });
            setLogoImageLoaded(false);
            setBrandingData((prev: any) => ({
                ...(prev || {}),
                media_id: uploaded.id,
                organization_media_details: {
                    ...(prev?.organization_media_details || {}),
                    ...uploaded,
                },
            }));
        }
    }, [mediaResponse]);

    const processLogoFile = (file?: File) => {
        if (!file) return;
        const formData = new FormData();
        formData.append("files", file);
        formData.append("type", "3");

        // Clear current preview to avoid showing stale image while upload is in progress
        setLogoImageLoaded(false);
        setImagePreview({
            url: "",
            name: "",
        });
        setBrandingData((prev: any) => ({
            ...(prev || {}),
            media_id: null,
        }));

        mediaApi(formData);
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        processLogoFile(file);
    };

    const handleLogoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLogoDragActive(true);
    };

    const handleLogoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLogoDragActive(false);
    };

    const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsLogoDragActive(false);
        const file = e.dataTransfer.files?.[0];
        processLogoFile(file);
    };

    const handleLogoAreaClick = () => {
        document.getElementById("brandingLogoUpload")?.click();
    };


    /**
  * @Call all organization data  using @viewProfileApi constant
  */
    useEffect(() => {
        if (userData?.role_id === 2) {
            const timer = setTimeout(() => {
                viewProfileApi();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [activeStep === 0, qrSubScreen === 'single-preview']);

    const handleStepClick = (stepIndex: number) => {
        // Can only navigate to steps that have been completed or the next step after the last completed one
        if (stepIndex <= maxStepReached) {
            setActiveStep(stepIndex);
            // Reset QR sub-screen when navigating to step 1
            if (stepIndex === 1) {
                setQrSubScreen('selection');
                setSelectedQRType(null);
            }
        }
    };

    const handleContinue = () => {
        // Step 1: save branding before moving ahead
        if (activeStep === 0 && brandingData) {
            const payload = {
                // only send fields that can be edited here
                theme_color: brandingData.theme_color,
                font_color: brandingData.font_color,
                media_id: brandingData.media_id ?? null,
                suggested_amount_1: brandingData.suggested_amount_1 || null,
                suggested_amount_2: brandingData.suggested_amount_2 || null,
                suggested_amount_3: brandingData.suggested_amount_3 || null,
                show_notes: brandingData.show_notes ?? 0,
            };

            updateProfileApi(payload).then(() => {
                trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICKED_WIZARD_TIPPING_SCREEN);
                const nextStep = 1;
                setActiveStep(nextStep);
                setMaxStepReached((prev) => Math.max(prev, nextStep));
            });
            return;
        }

        if (activeStep === 1 && qrSubScreen === 'single-preview') {
            trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICKED_WIZARD_TEST_QR);
            setActiveStep(2);
            setMaxStepReached((prev) => Math.max(prev, 2));
            return;
        }

        if (activeStep === 1 && qrSubScreen === 'selection') {
            // If on QR selection screen, move to share screen (step 2)
            setActiveStep(2);
            setMaxStepReached((prev) => Math.max(prev, 2));
            return;
        }
        if (isLastStep) {
            trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICK_WIZARD_FINISH);
            goToDashboard();
            return;
        }
        const nextStep = Math.min(activeStep + 1, steps.length - 1);
        setActiveStep(nextStep);
        setMaxStepReached((prev) => Math.max(prev, nextStep));
    };

    const handleBack = () => {
        if (activeStep === 1) {
            if (qrSubScreen === 'single-preview') {
                goToDashboard();
                return;
            }
            if (qrSubScreen === 'multiple-dashboard') {
                setQrSubScreen('selection');
                setSelectedQRType(null);
                return;
            }
        }
        if (activeStep === 0) {
            goToDashboard();
            return;
        }
        if (activeStep === 2) {
            // From share screen, go back to QR selection
            setActiveStep(1);
            setQrSubScreen('selection');
            setSelectedQRType(null);
            return;
        }
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const showNotesSelectValue = useMemo(
        () =>
            showNotesOptions.find(
                (option) => option.value === (brandingData?.show_notes ?? 1),
            ) || showNotesOptions[0],
        [brandingData?.show_notes],
    );

    const showNotesFormikAdapter = useMemo(
        () =>
        ({
            values: {
                show_notes_select: showNotesSelectValue,
            },
            touched: {},
            errors: {},
            setFieldValue: (_name: string, option: any) => {
                setBrandingData((prev: any) => ({
                    ...(prev || {}),
                    show_notes: option?.value ?? 0,
                }));
            },
        }),
        [showNotesSelectValue],
    );

    useEffect(() => {
        if (!suggestedInitRef.current && brandingData) {
            suggestedInitRef.current = true;
            const hasSuggested =
                !!brandingData?.suggested_amount_1 ||
                !!brandingData?.suggested_amount_2 ||
                !!brandingData?.suggested_amount_3;
            setShowSuggestedFields(hasSuggested);
        }
    }, [brandingData]);

    const handleSuggestedToggle = () => {
        setShowSuggestedFields((prevVisible) => !prevVisible);
    };

    const handleTippingViewNoteToggle = () => {
        setShowTippingViewNote((prevVisible) => {
            const nextVisible = !prevVisible;
            // Keep brandingData.show_notes in sync with the toggle
            setBrandingData((prev: any) => ({
                ...(prev || {}),
                show_notes: nextVisible ? 1 : 0,
            }));
            return nextVisible;
        });
    };

    const hasSuggestedTipValues =
        !!brandingData?.suggested_amount_1 ||
        !!brandingData?.suggested_amount_2 ||
        !!brandingData?.suggested_amount_3;

    const renderStepContent = () => {
        if (activeStep === 0) {
            return (
                <> <h3 className={styles.stepTitle}>Tipping Screen</h3>
                    <p className={styles.stepDescription}>Your tipping screen is what your customers will see. You can customize it with your own logo,</p><p className={styles.stepDescriptionBottom}> or use the TopTipper logo provided.</p>
                    <div className={styles.customizeGrid}>
                        <div className={styles.formContainer}>
                            <form className={styles.tipperContainer}>
                                <div className={styles.tipperBottom}>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            padding: "20px",
                                            paddingTop: '0px'
                                        }}
                                    >
                                        <div
                                            className={styles.circle}
                                            style={{
                                                backgroundColor: brandingData?.theme_color || "#EEF3FF",
                                                position: 'relative',
                                            }}
                                        >
                                            {mediaLoading ? (
                                                <Circles
                                                    height="40"
                                                    width="40"
                                                    color="#2549E8"
                                                    ariaLabel="logo-upload-loading"
                                                    visible
                                                    wrapperStyle={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                    }}
                                                />
                                            ) : imagePreview?.url ? (
                                                <img
                                                    src={imagePreview.url}
                                                    alt={imagePreview.name || "logo"}

                                                />
                                            ) : (
                                                <span >
                                                    <img src={toptipper} alt="" />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <h5 className="">
                                        {brandingData?.name || data?.name || ""}
                                    </h5>
                                    {(showSuggestedFields && hasSuggestedTipValues) && (
                                        <div className={styles.suggestedAmount}>
                                            <h6>Tip amount ($)</h6>
                                            <div className={styles.suggestCards}>
                                                {!!brandingData?.suggested_amount_1 && (
                                                    <p
                                                        style={{ cursor: 'not-allowed' }}
                                                    >
                                                        {formatAmountDisplay(brandingData.suggested_amount_1)}
                                                    </p>
                                                )}
                                                {!!brandingData?.suggested_amount_2 && (
                                                    <p style={{ cursor: 'not-allowed' }}
                                                    >
                                                        {formatAmountDisplay(brandingData.suggested_amount_2)}
                                                    </p>
                                                )}
                                                {!!brandingData?.suggested_amount_3 && (
                                                    <p style={{ cursor: 'not-allowed' }}
                                                    >
                                                        {formatAmountDisplay(brandingData.suggested_amount_3)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.tipperRightForm} style={{ marginTop: showSuggestedFields ? '7px' : '' }}>
                                        {" "}
                                        <label htmlFor="">
                                            {showSuggestedFields && hasSuggestedTipValues
                                                ? "Custom amount ($)"
                                                : "Tip amount ($)"}
                                        </label>
                                        <br />
                                        <div className={styles.search}>
                                            <NumericFormat
                                                allowNegative={false}
                                                style={{
                                                    cursor:
                                                        "not-allowed"
                                                }}
                                                disabled
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                type="text"
                                                placeholder="Enter Amount"
                                            />
                                        </div>
                                        {brandingData?.show_notes === 1 && (
                                            <div className="my-2">
                                                <label htmlFor="note">
                                                    Note <span>(Optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="note"
                                                    disabled
                                                    placeholder="Enter note"
                                                    className={styles.email}
                                                    style={{ marginBottom: '2px', cursor: 'not-allowed' }}
                                                />

                                            </div>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            justifyContent: "center",
                                            marginTop: "15px",
                                            padding: "0px 18px",
                                        }}
                                    >
                                        <ThemeButton
                                            style={{
                                                width: '100%',
                                                cursor: 'not-allowed',
                                            }}

                                        >
                                            Send Tip
                                        </ThemeButton>
                                    </div>
                                </div>
                            </form>
                            <PaymentHeader />
                        </div>
                        <div className={styles.brandingPanel}>
                            <h3>Setup Branding</h3>
                            <p className={styles.hint}>Logo (optional)</p>
                            <div
                                className={styles.uploadArea}
                                onClick={handleLogoAreaClick}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleLogoAreaClick();
                                    }
                                }}
                                onDragOver={handleLogoDragOver}
                                onDragLeave={handleLogoDragLeave}
                                onDrop={handleLogoDrop}
                                role="button"
                                tabIndex={0}
                                style={
                                    isLogoDragActive
                                        ? { borderColor: "#2549E8", backgroundColor: "#F4F7FF" }
                                        : undefined
                                }
                            >
                                <img
                                    src={uploadIcon}
                                    alt="upload"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLogoAreaClick();
                                    }}
                                    style={{ cursor: "pointer" }}
                                />
                                <p>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleLogoAreaClick();
                                        }}
                                    >
                                        Drag & Drop files here or
                                    </button>
                                    <label
                                        className={styles.linkButton}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleLogoAreaClick();
                                        }}
                                    >
                                        Choose File
                                    </label>
                                    <input
                                        id="brandingLogoUpload"
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        style={{ display: "none" }}
                                        onChange={handleLogoChange}
                                    />
                                </p>

                            </div>


                            {/* <div className={styles.colorGrid}>
                                <ColorInput
                                    label="Color (HEX)"
                                    placeholder="#000000"
                                    field="theme_color"
                                    value={brandingData?.theme_color || ""}
                                />
                                <ColorInput
                                    label="Font (HEX)"
                                    placeholder="#000000"
                                    field="font_color"
                                    value={brandingData?.font_color || ""}
                                />
                            </div> */}

                            <label className={styles.toggleLabel}>
                                <button
                                    type="button"
                                    onClick={handleSuggestedToggle}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        padding: 0,
                                        marginRight: "8px",
                                        cursor: "pointer",
                                    }}
                                >
                                    <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect width="32" height="24" rx="12" fill={showSuggestedFields ? "#3E7BF4" : "#E0E0E0"} />
                                        <rect x={showSuggestedFields ? 12 : 4} y="4" width="16" height="16" rx="8" fill="white" />
                                    </svg>
                                </button>
                                Show Suggested Tip Amounts
                            </label>
                            {showSuggestedFields && (
                                <div className="d-flex gap-3">
                                    <AmountField
                                        name="suggested_amount_1"
                                        label="Suggested Tip 1"
                                        placeholder="0.00"
                                        icon={dollarIcon}
                                        max={4}
                                        className={styles.suggestedTips}
                                        value={brandingData?.suggested_amount_1 ?? ""}
                                        onChange={handleSuggestedAmountChange('suggested_amount_1')}
                                        onBlur={handleSuggestedAmountChange('suggested_amount_1')}
                                        type="text"
                                    />
                                    <AmountField
                                        name="suggested_amount_2"
                                        label="Suggested Tip 2"
                                        placeholder="0.00"
                                        icon={dollarIcon}
                                        max={4}
                                        className={styles.suggestedTips}
                                        value={brandingData?.suggested_amount_2 ?? ""}
                                        onChange={handleSuggestedAmountChange('suggested_amount_2')}
                                        onBlur={handleSuggestedAmountChange('suggested_amount_2')}
                                        type="text"
                                    />
                                    <AmountField
                                        name="suggested_amount_3"
                                        label="Suggested Tip 3"
                                        placeholder="0.00"
                                        icon={dollarIcon}
                                        max={4}
                                        className={styles.suggestedTips}
                                        value={brandingData?.suggested_amount_3 ?? ""}
                                        onChange={handleSuggestedAmountChange('suggested_amount_3')}
                                        onBlur={handleSuggestedAmountChange('suggested_amount_3')}
                                        type="text"
                                    />
                                </div>
                            )}

                            <div>
                                <label className={styles.toggleLabel} style={{ marginTop: showSuggestedFields ? '20px' : '0px' }}>
                                    <button
                                        type="button"
                                        onClick={handleTippingViewNoteToggle}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            padding: 0,
                                            marginRight: "8px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="32" height="24" rx="12" fill={showTippingViewNote ? "#3E7BF4" : "#E0E0E0"} />
                                            <rect x={showTippingViewNote ? 12 : 4} y="4" width="16" height="16" rx="8" fill="white" />
                                        </svg>
                                    </button>
                                    Show Tipping View Note
                                </label>
                            </div>


                        </div>
                    </div>
                </>
            );
        }

        if (activeStep === 1) {
            // Show selection screen
            if (qrSubScreen === 'selection') {
                return (
                    <>
                        <h3 className={styles.stepTitle}>Create your QR Codes</h3>
                        <p className={styles.stepDescription}>
                            With TopTipper, you can create separate QR codes for properties, employees, or services. Would </p>
                        <p className={styles.stepDescriptionBottom} >you like multiple QR codes or just one simple code?
                        </p>
                        <div className={styles.qrCardContainer}>
                            <button
                                type="button"
                                className={`${styles.qrOptionCard} ${selectedQRType === 'single' ? styles.active : ''}`}
                                onClick={() => {
                                    trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICKED_WIZARD_SINGLE_QR);
                                    setSelectedQRType('single');
                                    setQrSubScreen('single-preview');
                                }}
                            >
                                <div className={styles.diamondIcon}>
                                    <img src={singleQR} alt="" />
                                </div>
                                <span>Single QR Code</span>
                            </button>
                            <button
                                type="button"
                                className={`${styles.qrOptionCard} ${selectedQRType === 'multiple' ? styles.active : ''}`}
                                onClick={() => {
                                    trackWizardClick(WIZARD_TRACK_CLICK_TYPE.CLICKED_WIZARD_MULTIPLE_QR);
                                    setSelectedQRType('multiple');
                                    setQrSubScreen('multiple-dashboard');
                                }}
                            >
                                <div className={styles.diamondIcon}>
                                    <img src={multipleQR} alt="" />
                                </div>
                                <span>Multiple QR Codes</span>
                            </button>
                        </div>
                    </>
                );
            }

            // Show Single QR Code preview screen (Image 2)
            if (qrSubScreen === 'single-preview') {
                return (
                    <>
                        <h3 className={styles.stepTitle}>Test your Code and Link</h3>
                        <p className={styles.stepDescriptionBottom} style={{ fontSize: '18px', fontWeight: 500 }}>
                        Scan this QR Code with your phone, or click the link below, to open your tipping page and see what your customers will see. You can send yourself a small tip to see how it works.
                        </p>
                        <div className={styles.qrLayout}>
                            <div className={styles.qrPreview}>
                                <img src={viewProfile?.data?.qr_code} alt="QR Code" className={styles.qrCodeImage} />
                                <div className={styles.qrDetails}>
                                    <label>Sharable Link</label>
                                    <div className={styles.linkInput}>
                                        <input type="text" value={viewProfile?.data?.copy_link} readOnly />
                                        <span className={styles.copyIcon} onClick={() => { copyToClipBoard(viewProfile?.data?.copy_link) }}>{tippingCopy}</span>
                                    </div>
                                    {/* <button
                                        type="button"
                                        className={styles.downloadButton}
                                        onClick={() => downloadImage(viewProfile?.data?.qr_code, "qr-code.png")}
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M13 5V11H14.17L12 13.17L9.83 11H11V5H13ZM15 3H9V9H5L12 16L19 9H15V3ZM19 18H5V20H19V18Z" fill="#2549E8" />
                                        </svg>
                                        Download QR Code
                                    </button> */}
                                </div>
                            </div>
                        </div>
                        <div className={styles.feeNotice}>
                            {isFeePaidByOrganization(viewProfile?.data?.fee_paid_by) && (
                                <p className={styles.feeNoticeTip}>
                                    <strong>Tip:</strong> Fees come out of each tip, so a $5 tip lands as about $4.06. Want tippers to cover it? Email <a href="mailto:support@toptipper.co">support@toptipper.co</a>.
                                </p>
                            )}
                        </div>
                    </>
                );
            }

            // Show Multiple QR Codes - Go to Dashboard screen (Image 3)
            if (qrSubScreen === 'multiple-dashboard') {
                return (
                    <>
                        <div className={styles.dashboardTopSection}>
                            <h3 className={styles.stepTitle}>Go to the Dashboard</h3>
                            <p className={styles.stepDescription}>
                                To create multiple QR Codes for different properties, employees, or services, go to the Dashboard
                                and Add a Property. Once you have added your properties, you can add employees or services to
                                each property. Here’s a video to show you how it’s done.
                            </p>
                            <div className={styles.videoEmbed}>
                                <iframe
                                    src="https://www.youtube.com/embed/O8If9lB9zgM"
                                    title="TopTipper Dashboard Setup"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                        <div className={styles.dashboardBottomSection}>
                            <p className={styles.appointmentText}>
                                If you have many properties and would like to book a concierge setup
                            </p>
                            <div className={styles.appointmentButtonWrap}>
                                <a
                                    href="https://cal.com/david_b_waxman"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.appointmentButton}
                                >
                                    Book an Appointment
                                </a>
                            </div>
                        </div>
                    </>
                );
            }
        }

        // Step 2: Share your QR Codes (Image 4)
        if (activeStep === 2) {
            return (
                <>
                    <h3 className={styles.stepTitle}>Share your QR Codes</h3>
                    <p className={styles.stepDescription}>
                        Print a sign with your unique QR Code. For more options or to custom order a wind sign, wooden sign, or business cards, </p>
                    <p className={styles.stepDescriptionBottom} > go to the Dashboard and click the Signs Menu.
                    </p>
                    <div className={styles.signPreview}>
                        <div className={styles.signCard} ref={signCardRef}>
                            <div className={styles.pdfWrapper}>
                                {signPdfLoading && (
                                    <div className={styles.pdfLoader}>
                                        <Circles
                                            height="40"
                                            width="40"
                                            color="#2549E8"
                                            ariaLabel="pdf-loading"
                                            visible
                                        />
                                    </div>
                                )}
                                {signData?.[0]?.url ? (
                                    useIframePreview ? (
                                        <iframe
                                            src={`${signData?.[0]?.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                            title="Sign PDF Preview"
                                            className={styles.pdfIframe}
                                            onLoad={() => {
                                                setSignPdfLoading(false);
                                                setSignPdfError("");
                                            }}
                                        />
                                    ) : (
                                        <Document
                                            key={signData?.[0]?.url}
                                            file={signData?.[0]?.url}
                                            onLoadSuccess={() => {
                                                setSignPdfLoading(false);
                                                setSignPdfError("");
                                            }}
                                            onLoadError={(error) => {
                                                setSignPdfLoading(false);
                                                setUseIframePreview(true);
                                                setSignPdfError("Failed to load PDF preview");
                                                notifyBugsnagError(error as any, {
                                                    page: "tippingExperience",
                                                    action: "loadSignPdfPreview",
                                                    pdfUrl: signData?.[0]?.url,
                                                });
                                            }}
                                            loading={null}
                                        >
                                            <Page
                                                pageNumber={1}
                                                width={pdfWidth}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        </Document>
                                    )
                                ) : (
                                    !signPdfLoading && (
                                        <div className={styles.pdfUnavailable}>
                                            <p>{signPdfError || "PDF not available"}</p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                        <div className={styles.signActions}>
                            <button
                                type="button"
                                className={styles.printButton}
                                onClick={handlePrintClick}
                                disabled={!signData?.[0]?.url || printDisabled}
                            >
                                {PrintIcon}

                                Print Sign
                            </button>
                            <button
                                type="button"
                                className={styles.printButton}
                                onClick={handleDownloadClick}
                                disabled={!signData?.[0]?.url || downloadDisabled}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13 5V11H14.17L12 13.17L9.83 11H11V5H13ZM15 3H9V9H5L12 16L19 9H15V3ZM19 18H5V20H19V18Z" fill="#2549E8" />
                                </svg>
                                Download Sign
                            </button>
                        </div>
                    </div>
                </>
            );
        }

        return null;
    };

    return (

        <div className={styles.page}>
            <TippingJourneyLayout
                steps={steps}
                activeStep={activeStep === 2 ? 2 : activeStep}
                maxStepReached={maxStepReached}
                onStepClick={handleStepClick}
                footer={
                    <div className={styles.footerControls}>
                        {activeStep === 1 && qrSubScreen === 'multiple-dashboard' ? (
                            <>
                                <button
                                    type="button"
                                    className={styles.backButton}
                                    onClick={handleBack}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7.83 11L11.41 7.41L10 6L4 12L10 18L11.41 16.59L7.83 13H20V11H7.83Z" fill="#2549E8" />
                                    </svg>
                                    Back
                                </button>
                                <ThemeButton onClick={goToDashboard} className={styles.continueButton} loading={countLoading} disabled={countLoading}>
                                    {tippingNavigate} Go to Dashboard
                                </ThemeButton>
                            </>
                        ) : (
                            <>
                                {isLastStep || !(activeStep === 1 && qrSubScreen === 'multiple-dashboard') ? <div></div> : (
                                    <button
                                        type="button"
                                        className={styles.backButton}
                                        onClick={handleBack}
                                    >
                                        Back
                                    </button>
                                )}

                                <ThemeButton
                                    onClick={handleContinue}
                                    className={styles.continueButton}
                                    loading={activeStep === 0 ? updateProfileLoading : isLastStep ? countLoading : false}
                                    disabled={(activeStep === 0 && updateProfileLoading) || (isLastStep && countLoading)}
                                >
                                    {isLastStep ? "Finished" : <>{tippingNavigate} Continue</>}
                                </ThemeButton>
                            </>
                        )}
                    </div>
                }
            >
                {renderStepContent()}
            </TippingJourneyLayout>
        </div>

    );
};

export default TippingExperience;

